/**
 * Health + system information endpoints used by the dashboard's HealthCard.
 *
 * The backend keeps the public `/health` endpoint deliberately small (just
 * `status`, `version`, `uptime`) so unauthenticated probes stay cheap. Optional
 * fields (Go runtime stats, GC stats) are declared on the TypeScript type as
 * optional so the component can gracefully fall back to `—` if the backend
 * later decides to expose them — no frontend change required.
 */

import api from './client'

/** Raw payload returned by `GET /health` (public, no auth). */
export interface HealthStatus {
  status: string
  version: string
  /** Process uptime in Go's `time.Duration` string form, e.g. "2h3m4.567s". */
  uptime: string
  /** Below fields are optional — backend may extend `/health` without a frontend change. */
  goVersion?: string
  numGoroutine?: number
  memAllocMB?: number
  memSysMB?: number
  numGC?: number
  gcPauseNs?: number
  /** Extra arbitrary fields the backend might add (forward-compat). */
  [key: string]: unknown
}

/** Payload returned by `GET /api/version` (public, no auth). */
export interface SystemVersion {
  product: string
  version: string
}

/** Payload returned by `GET /api/version/info` (auth required). */
export interface VersionInfo {
  version: string
  enabledFeatures: string[]
}

/** Public health probe — used by the dashboard status dot and uptime label. */
export async function getHealth(): Promise<HealthStatus> {
  return api.get<HealthStatus>('/health', { silent: true })
}

/** Product + version (sidebar overlay source). */
export async function getSystemVersion(): Promise<SystemVersion> {
  return api.get<SystemVersion>('/api/version', { silent: true })
}

/** AFD-compatible version + enabled feature flags. */
export async function getVersionInfo(): Promise<VersionInfo> {
  // v3.13.92 fix: 此前未解包 {ok, data} 信封 — versionInfo 恒为信封对象,
  // enabledFeatures 永远 undefined, Dashboard "功能特性" 列表恒空。
  const res = await api.get<{ ok: boolean; data: VersionInfo }>('/api/version/info', { silent: true })
  if (res && typeof res === 'object' && 'data' in res && res.data) {
    return (res as { data: VersionInfo }).data
  }
  return res as unknown as VersionInfo
}

/**
 * Parse a Go `time.Duration` string (e.g. "2h3m4.567s") into a compact
 * human-readable form: "2d 3h 45m" / "3h 12m" / "12m 30s" / "45s".
 * Returns the original string if the input is not a Go duration we can parse.
 */
export function formatUptime(raw: string | undefined | null): string {
  if (!raw) return '—'
  // Go duration: optional sign + sequence of <num><unit> where unit is one of
  // "ns", "us" / "µs", "ms", "s", "m", "h". We only care about the largest
  // three present.
  const re = /(-?\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)/g
  let ms = 0
  let matched = false
  let m: RegExpExecArray | null
  while ((m = re.exec(raw)) !== null) {
    matched = true
    const n = parseFloat(m[1])
    if (!Number.isFinite(n)) continue
    switch (m[2]) {
      case 'ns': ms += n / 1e6; break
      case 'us':
      case 'µs': ms += n / 1e3; break
      case 'ms': ms += n; break
      case 's':  ms += n * 1000; break
      case 'm':  ms += n * 60_000; break
      case 'h':  ms += n * 3_600_000; break
    }
  }
  if (!matched) return raw

  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const d = Math.floor(totalSec / 86400)
  const h = Math.floor((totalSec % 86400) / 3600)
  const min = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60

  if (d > 0) return `${d}d ${h}h ${min}m`
  if (h > 0) return `${h}h ${min}m`
  if (min > 0) return `${min}m ${s}s`
  return `${s}s`
}
